import { useCallback } from 'react';
import styled from 'styled-components';

import { EntitlementType } from '@dreryk/drerykweb-medical-dictionaries-api';
import { Patient } from '@dreryk/egabinet-patient-frontend-api';

import { ConfirmingDocumentNumberTextInput } from '../confirmingDocument/ConfirmingDocumentNumberTextInput';
import {
   ConfirmingDocumentTypeSelect,
   OnConfirmingDocumentTypeSelectItemsChanged,
} from '../confirmingDocument/ConfirmingDocumentTypeSelect';
import { EntitlementDocumentDateFields } from '../confirmingDocument/EntitlementDocumentDateFields/EntitlementDocumentDateFields';
import { PregnancyDateFields } from '../confirmingDocument/PregnancyDateFields/PregnancyDateFields';

import {
   useCqrsCreatePatientEntitlementCommand,
   usePatientEntitlementCreateFormController,
} from './PatientEntitlementCreateForm.hooks';
import {
   OnPatientEntitlementDocumentCreated,
   PatientEntitlementCreateFormRenderer,
} from './PatientEntitlementCreateForm.types';

const StyledPatientEntitlementForm = styled.form`
   display: grid;
   grid-gap: 1em;
   grid-template-columns: 1fr 1fr;

   > [class*='Status'] {
      justify-content: flex-start;
      grid-column: 1/-1;
   }
`;

StyledPatientEntitlementForm.displayName = 'StyledPatientEntitlementForm';

type PatientEntitlementCreateFormProps = {
   readonly entitlementType: EntitlementType;
   readonly patientId: Patient['id'];
   readonly onPatientEntitlementsDocumentCreated: OnPatientEntitlementDocumentCreated;
   readonly children: PatientEntitlementCreateFormRenderer;
};

export const PatientEntitlementCreateForm = (props: PatientEntitlementCreateFormProps): JSX.Element => {
   const { register, handleSubmit, setValue, formState, watch } = usePatientEntitlementCreateFormController({
      entitlementType: props.entitlementType,
   });

   const { executeAsync: createPatientEntitlement } = useCqrsCreatePatientEntitlementCommand({
      entitlementType: props.entitlementType,
      patientId: props.patientId,
      onPatientEntitlementDocumentCreated: props.onPatientEntitlementsDocumentCreated,
   });

   const handleConfirmingDocumentTypeSelectItemsChanged: OnConfirmingDocumentTypeSelectItemsChanged = useCallback(
      (items) => {
         if (items.length === 1) {
            setValue('confirmingDocumentType', items[0], { shouldDirty: false });
         }
      },
      [setValue]
   );

   const patientEntitlementCreate = handleSubmit(async (data) => {
      await createPatientEntitlement(data);
   });

   return (
      <StyledPatientEntitlementForm noValidate>
         <ConfirmingDocumentTypeSelect
            register={register}
            entitlementType={props.entitlementType}
            onConfirmingDocumentTypeSelectItemsChanged={handleConfirmingDocumentTypeSelectItemsChanged}
         />
         <ConfirmingDocumentNumberTextInput register={register} />
         {props.entitlementType === 'C' ? (
            <PregnancyDateFields register={register} watch={watch} />
         ) : (
            <EntitlementDocumentDateFields entitlementType={props.entitlementType} register={register} />
         )}

         {props.children({ patientEntitlementCreate: patientEntitlementCreate, shouldConfirmExit: formState.isDirty })}
      </StyledPatientEntitlementForm>
   );
};
